import { z } from 'zod';
import { schemaDomaine, schemaPermission } from './habilitations';

const code = z.string().regex(/^[a-z][a-z0-9_]{1,62}$/, 'Code en minuscules, chiffres et soulignés');

/** Actions automatiques qu'une transition peut déclencher (moteur de workflow, brief L0-C). */
export const ACTIONS_WORKFLOW = [
  'numeroter',
  'notifier',
  'generer_document',
  'calculer_echeance',
  'suspendre_delais',
  'reprendre_delais',
  'cloturer',
] as const;
export type ActionWorkflow = (typeof ACTIONS_WORKFLOW)[number];

export const TYPES_EVENEMENT_WORKFLOW = ['demarrage', 'transition', 'retour', 'migration_version', 'reouverture'] as const;
export type TypeEvenementWorkflow = (typeof TYPES_EVENEMENT_WORKFLOW)[number];

export const schemaPermissionRequise = z.object({
  domaine: schemaDomaine,
  permission: schemaPermission,
});

export const schemaEtatWorkflow = z.object({
  code,
  libelle: z.string().min(1).max(120),
  terminal: z.boolean(),
  /** Code du statut visible par l'usager (H3-03), nul pour les objets sans usager. */
  statut_usager: z.string().regex(/^[a-z_]+$/).nullable(),
  /** Étape de démarche dont l'état est issu (définitions générées par l'éditeur de démarches, lot L1-A). */
  etape: z
    .object({
      code: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/),
      nature: z.enum(['instruction', 'validation', 'decision']),
      rang: z.number().int().min(1),
      service_id: z.string().uuid().nullable(),
      role_code: z.string().nullable(),
      delai_interne_jours: z.number().int().nullable(),
    })
    .optional(),
});

export const schemaTransitionWorkflow = z.object({
  code,
  libelle: z.string().min(1).max(120),
  de: code,
  vers: code,
  /** Permission exigée ; nulle uniquement pour une transition automatique (exécutée par le serveur). */
  permission: schemaPermissionRequise.nullable(),
  automatique: z.boolean().default(false),
  motif_obligatoire: z.boolean().default(false),
  /** Codes de types de pièces qui doivent accompagner la transition. */
  pieces_requises: z.array(z.string().regex(/^[a-z][a-z0-9_]{1,62}$/)).default([]),
  actions: z.array(z.enum(ACTIONS_WORKFLOW)).default([]),
});

export const schemaDefinitionWorkflow = z
  .object({
    etat_initial: code,
    etats: z.array(schemaEtatWorkflow).min(1),
    transitions: z.array(schemaTransitionWorkflow),
    /** Permission d'un retour motivé vers un état déjà parcouru. */
    permission_retour: schemaPermissionRequise,
    /** Permission et états cibles d'une réouverture (depuis un état terminal). */
    reouverture: z
      .object({
        permission: schemaPermissionRequise,
        depuis: z.array(code).min(1),
        vers: code,
      })
      .nullable()
      .default(null),
  })
  .superRefine((d, ctx) => {
    const etats = new Set<string>();
    for (const e of d.etats) {
      if (etats.has(e.code)) ctx.addIssue({ code: 'custom', message: `État en double : ${e.code}`, path: ['etats'] });
      etats.add(e.code);
    }
    if (!etats.has(d.etat_initial)) ctx.addIssue({ code: 'custom', message: 'État initial inconnu', path: ['etat_initial'] });
    const cles = new Set<string>();
    d.transitions.forEach((t, i) => {
      if (!etats.has(t.de)) ctx.addIssue({ code: 'custom', message: `État source inconnu : ${t.de}`, path: ['transitions', i, 'de'] });
      if (!etats.has(t.vers)) ctx.addIssue({ code: 'custom', message: `État cible inconnu : ${t.vers}`, path: ['transitions', i, 'vers'] });
      const cle = `${t.code}:${t.de}`;
      if (cles.has(cle)) ctx.addIssue({ code: 'custom', message: `Transition en double : ${cle}`, path: ['transitions', i] });
      cles.add(cle);
      if (t.permission === null && !t.automatique) {
        ctx.addIssue({ code: 'custom', message: 'Une transition manuelle exige une permission', path: ['transitions', i, 'permission'] });
      }
      const source = d.etats.find((e) => e.code === t.de);
      if (source?.terminal) {
        ctx.addIssue({ code: 'custom', message: `Aucune transition ne part d'un état terminal (${t.de})`, path: ['transitions', i] });
      }
    });
    if (d.reouverture) {
      for (const e of d.reouverture.depuis) {
        if (!etats.has(e)) ctx.addIssue({ code: 'custom', message: `État de réouverture inconnu : ${e}`, path: ['reouverture'] });
      }
      if (!etats.has(d.reouverture.vers)) ctx.addIssue({ code: 'custom', message: 'État cible de réouverture inconnu', path: ['reouverture'] });
    }
  });
export type DefinitionWorkflow = z.infer<typeof schemaDefinitionWorkflow>;
export type TransitionWorkflow = DefinitionWorkflow['transitions'][number];
export type EtatWorkflow = DefinitionWorkflow['etats'][number];

/** Événement d'historique tel que retourné par l'API. */
export const schemaEvenementWorkflow = z.object({
  id: z.string().uuid(),
  type: z.enum(TYPES_EVENEMENT_WORKFLOW),
  transition_code: z.string().nullable(),
  etat_avant: z.string().nullable(),
  etat_apres: z.string(),
  horodatage: z.string(),
  motif: z.string().nullable(),
  documents: z.array(z.string().uuid()),
  delegation_id: z.string().uuid().nullable(),
  actions: z.array(z.unknown()),
  /** Données de l'acte (résultat de décision, service destinataire, preuve d'envoi...). */
  donnees: z.record(z.string(), z.unknown()),
  auteur: z
    .object({ compte_id: z.string().uuid().nullable(), appartenance_id: z.string().uuid().nullable(), nom: z.string().nullable() })
    .nullable(),
  service_id: z.string().uuid().nullable(),
});
export type EvenementWorkflow = z.infer<typeof schemaEvenementWorkflow>;

export const schemaHistorique = z.object({ elements: z.array(schemaEvenementWorkflow) });

export const schemaRetour = z.object({
  vers: code,
  motif: z.string().trim().min(3, 'Motif obligatoire').max(2000),
});

export const schemaReouverture = z.object({
  motif: z.string().trim().min(3, 'Motif obligatoire').max(2000),
});
